import { createTheme } from '@mui/material/styles';
import { tokens } from './tokens';

// Minimal / flat design system.
// White surfaces, hairline borders, one accent color, generous whitespace, no gradients or blur.
// Component overrides here cascade to every page so the look stays consistent automatically.

export const nexusTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: tokens.color.accent, dark: tokens.color.accentHover },
    secondary: { main: '#0F1115' },
    error: { main: tokens.color.danger },
    warning: { main: tokens.color.warning },
    success: { main: tokens.color.success },
    info: { main: tokens.color.info },
    background: {
      default: tokens.color.bg,
      paper: tokens.color.surface,
    },
    text: {
      primary: tokens.color.text,
      secondary: tokens.color.textSecondary,
    },
    divider: tokens.color.border,
  },
  shape: {
    borderRadius: tokens.radius.md,
  },
  typography: {
    fontFamily: tokens.font.family,
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontWeight: 650, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 650 },
    subtitle2: { fontWeight: 600 },
    overline: { fontWeight: 600, letterSpacing: '0.08em' },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: 0,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.color.bg,
          backgroundImage: 'none',
          color: tokens.color.text,
          WebkitFontSmoothing: 'antialiased',
        },
        '#root': { minHeight: '100vh' },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(15,17,21,0.16)',
          borderRadius: 999,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(15,17,21,0.28)' },
      },
    },

    // Flat surfaces — borders instead of shadows.
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: tokens.color.border },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined', elevation: 0 },
      styleOverrides: {
        root: {
          borderColor: tokens.color.border,
          borderRadius: tokens.radius.lg,
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { '&:last-child': { paddingBottom: 16 } } },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          padding: '8px 14px',
          minHeight: 38,
        },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        outlined: {
          backgroundColor: tokens.color.surface,
          borderColor: tokens.color.borderStrong,
          color: tokens.color.text,
          '&:hover': { backgroundColor: tokens.color.neutralSoft, borderColor: tokens.color.borderStrong },
        },
        text: { '&:hover': { backgroundColor: tokens.color.neutralSoft } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.md },
      },
    },

    // Inputs / dropdowns — consistent hairline fields everywhere.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.surface,
          borderRadius: tokens.radius.md,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.color.borderStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.color.textMuted },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.accent,
            borderWidth: 1,
          },
        },
        input: { padding: '10px 12px' },
        inputSizeSmall: { padding: '7px 10px' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: tokens.color.textSecondary } } },
    MuiSelect: {
      defaultProps: { MenuProps: { disablePortal: true } },
      styleOverrides: { select: { display: 'flex', alignItems: 'center' } },
    },

    // Menus / popovers — the one place elevation is allowed.
    MuiMenu: {
      defaultProps: { disablePortal: true },
      styleOverrides: {
        paper: {
          marginTop: 4,
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.color.border}`,
          boxShadow: tokens.shadow.pop,
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          minHeight: 36,
          fontSize: 14,
          '&.Mui-selected': { backgroundColor: tokens.color.accentSoft },
          '&.Mui-selected:hover': { backgroundColor: tokens.color.accentSoft },
        },
      },
    },
    MuiPopover: {
      defaultProps: { disablePortal: true },
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.color.border}`,
          boxShadow: tokens.shadow.pop,
        },
      },
    },
    MuiPopper: { defaultProps: { disablePortal: true } },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.color.border}`,
          boxShadow: tokens.shadow.pop,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { backgroundColor: tokens.color.surface } },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.sm, fontWeight: 600, height: 24 },
        outlined: { borderColor: tokens.color.border, backgroundColor: tokens.color.surface },
        sizeSmall: { height: 22, fontSize: 12 },
      },
    },

    MuiTooltip: {
      defaultProps: { PopperProps: { disablePortal: true }, arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0F1115',
          fontSize: 12,
          fontWeight: 500,
          borderRadius: tokens.radius.sm,
          padding: '6px 9px',
        },
        arrow: { color: '#0F1115' },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: tokens.color.border } } },

    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: tokens.color.border, padding: '12px 14px' },
        head: {
          backgroundColor: tokens.color.surfaceMuted,
          color: tokens.color.textSecondary,
          fontWeight: 600,
          fontSize: 12,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, minHeight: 44 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2, borderRadius: 2 },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.surface,
          borderBottom: `1px solid ${tokens.color.border}`,
          backgroundImage: 'none',
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, backgroundColor: tokens.color.neutralSoft, height: 8 },
        bar: { borderRadius: 999 },
      },
    },
  },
});
